import { useCallback } from 'react';

import { toast } from '@/common/components/shared/toaster/toast';
import { type EmployeeGender, useUpdateEmployeePersonalInformationMutation } from '@/common/graphql';
import type { EmployeePersonalInformationFormDataType } from '@/common/types';
import { ApiUtils, StringUtils } from '@/common/utils';

interface HookProps {
  onCompleted: VoidFunction;
}

export const useUpdateEmployeePersonalInformation = ({ onCompleted }: HookProps) => {
  const [updateRecord, { loading }] = useUpdateEmployeePersonalInformationMutation();

  const handleUpdateEmployeePersonalInformation = useCallback(
    async (formData: EmployeePersonalInformationFormDataType, employeeId: string) => {
      await updateRecord({
        variables: {
          input: {
            bank: {
              accountHolderName: formData.accountHolderName,
              accountNumber: formData.accountNumber,
              branchName: formData.branchName,
              countryId: formData.bankCountryCode,
              name: formData.bankName,
            },
            consultingFeeBank: {
              accountHolderName: formData.consultingFeeAccountHolderName,
              accountNumber: formData.consultingFeeAccountNumber,
              branchName: formData.consultingFeeBranchName,
              countryId: formData.consultingFeeBankCountryCode,
              currencyId: formData.consultingFeeBankCurrencyId,
              name: formData.consultingFeeBankName,
            },
            currentAddress: formData.currentAddress,
            dateOfBirth: StringUtils.emptyToNull(formData.dateOfBirth),
            employeeId: employeeId,
            gender: formData.gender as EmployeeGender,
            idExpiryDate: StringUtils.emptyToNull(formData.idExpiryDate),
            idIssuedDate: StringUtils.emptyToNull(formData.idIssuedDate),
            idIssuedPlace: formData.idIssuedPlace,
            idNumber: formData.idNumber,
            idType: formData.idType,
            maritalStatus: formData.maritalStatus,
            nhiDependents: formData.nhiDependents,
            passportExpiryDate: StringUtils.emptyToNull(formData.passportExpiryDate),
            passportIssuedDate: StringUtils.emptyToNull(formData.passportIssuedDate),
            passportIssuedPlace: formData.passportIssuedPlace,
            passportName: formData.passportName,
            passportNumber: formData.passportNumber,
            passportPlaceOfBirth: formData.placeOfBirth,
            pensionVoluntaryContributionRate: formData.pensionVoluntaryContributionRate,
            permanentAddress: formData.permanentAddress,
            personalEmail: formData.personalEmail || null,
            personalPhoneNumber:
              formData.personalPhoneNumberCountryCode && formData.personalPhoneNumber
                ? {
                    countryId: formData.personalPhoneNumberCountryCode,
                    number: formData.personalPhoneNumber,
                  }
                : null,
            socialInsuranceNotes: formData.socialInsuranceNotes,
            socialInsuranceNumber: formData.socialInsuranceNumber,
            taxCode: formData.taxCode,
            taxWithholdingRate: formData.taxWithholdingRate,
            residentialCountryId: formData.residentialCountryId ?? '',
            remarks: formData.remarks,
          },
        },
        onCompleted: data => {
          if (data.updateEmployeePersonalInformation) {
            toast.success();
            onCompleted();
          }
        },
        onError: error => ApiUtils.handleApiError(error),
      });
    },
    [onCompleted, updateRecord],
  );

  return {
    handleUpdateEmployeePersonalInformation,
    updating: loading,
  };
};
